import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { permissionService } from '../services/permissionService';
import { useI18n } from '../hooks/useI18n';

const { Title, Text } = Typography;
const { Option } = Select;

interface Permission {
  id: number;
  permission_name: string;
  display_name: string;
  description?: string;
  category?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const PermissionsManager: React.FC = () => {
  const { t } = useI18n();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [permissionForm] = Form.useForm();

  // 权限分类选项
  const categoryOptions = [
    { value: 'admin', label: t('permissions.category.admin') },
    { value: 'edit', label: t('permissions.category.edit') },
    { value: 'view', label: t('permissions.category.view') },
    { value: 'other', label: t('permissions.category.other') },
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      const permissionsData = await permissionService.getPermissions();
      setPermissions(permissionsData);
    } catch (error) {
      console.error('Failed to load permissions:', error);
      message.error(t('permissions.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // 创建权限
  const handleCreatePermission = () => {
    permissionForm.resetFields();
    setEditingPermission(null);
    setPermissionModalVisible(true);
  };

  // 编辑权限
  const handleEditPermission = (permission: Permission) => {
    setEditingPermission(permission);
    permissionForm.setFieldsValue({
      name: permission.permission_name,
      displayName: permission.display_name,
      description: permission.description,
      category: permission.category,
      // 将数字 1/0 转换为布尔值 true/false
      isActive: Boolean(permission.is_active),
    });
    setPermissionModalVisible(true);
  };

  // 删除权限
  const handleDeletePermission = async (permissionId: number) => {
    try {
      await permissionService.deletePermission(permissionId.toString());
      message.success(t('permissions.deleteSuccess'));
      loadData();
    } catch (error) {
      console.error('Failed to delete permission:', error);
      message.error(t('permissions.deleteFailed'));
    }
  };

  // 创建/更新权限提交
  const handlePermissionSubmit = async (values: {
    name: string;
    displayName: string;
    description?: string;
    category: string;
    isActive?: boolean;
  }) => {
    try {
      // 确保 displayName 和 description 字段总是被传递，即使是空字符串
      const submitData = {
        ...values,
        displayName: values.displayName !== undefined ? (values.displayName || '') : values.displayName,
        description: values.description !== undefined ? (values.description || '') : undefined
      };
      
      console.log('提交权限数据:', submitData);
      
      if (editingPermission) {
        await permissionService.updatePermission(editingPermission.id.toString(), submitData);
        message.success(t('permissions.updateSuccess'));
      } else {
        await permissionService.createPermission(submitData);
        message.success(t('permissions.createSuccess'));
      }
      setPermissionModalVisible(false);
      loadData();
    } catch (error) {
      console.error('Failed to save permission:', error);
      message.error(editingPermission ? t('permissions.updateFailed') : t('permissions.createFailed'));
    }
  };

  // 重新加载数据
  const handleReload = async () => {
    try {
      await permissionService.reloadConfig();
      message.success(t('permissions.reloadSuccess'));
      loadData();
    } catch (error) {
      console.error('Failed to reload configuration:', error);
      message.error(t('permissions.reloadFailed'));
    }
  };

  // 权限表格列定义
  const permissionColumns = [
    {
      title: t('permissions.colPermissionName'),
      dataIndex: 'permission_name',
      key: 'permission_name',
      render: (permission_name: string) => (
        <Space>
          <SafetyOutlined />
          <Text code>{permission_name}</Text>
        </Space>
      ),
    },
    {
      title: t('permissions.colDisplayName'),
      dataIndex: 'display_name',
      key: 'display_name',
      render: (display_name: string) => (
        <Text strong>{display_name}</Text>
      ),
    },
    {
      title: t('permissions.colCategory'),
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => {
        const categoryColors: { [key: string]: string } = {
          admin: 'red',
          edit: 'orange',
          view: 'green',
          other: 'default',
        };
        return (
          <Tag color={categoryColors[category] || 'default'}>
            {category ? t(`permissions.category.${category}`) : t('permissions.category.other')}
          </Tag>
        );
      },
    },
    {
      title: t('permissions.colDescription'),
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <Text type="secondary">{description || t('permissions.noDescription')}</Text>
      ),
    },
    {
      title: t('permissions.colStatus'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active: boolean) => (
        <Tag color={is_active ? 'green' : 'red'}>
          {is_active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('permissions.colCreatedAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: string) => (
        <Text type="secondary">
          {new Date(created_at).toLocaleString()}
        </Text>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: unknown, record: Permission) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditPermission(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('permissions.confirmDelete')}
            onConfirm={() => handleDeletePermission(record.id)}
            okText={t('common.confirm')}
            cancelText={t('common.cancel')}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题和操作按钮 */}
      <div style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <SafetyOutlined style={{ marginRight: '8px' }} />
              {t('permissions.title')}
            </Title>
            <Text type="secondary">{t('permissions.subtitle')}</Text>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreatePermission}
              >
                {t('permissions.createPermission')}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReload}
              >
                {t('common.refresh')}
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('permissions.stat.totalPermissions')}
              value={permissions.length}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('permissions.stat.enabledPermissions')}
              value={permissions.filter(p => p.is_active).length}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('permissions.stat.disabledPermissions')}
              value={permissions.filter(p => !p.is_active).length}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 权限管理卡片 */}
      <Card title={t('permissions.card.permissions')}>
        <Table
          columns={permissionColumns}
          dataSource={permissions}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('common.paginationTotal', { from: range[0], to: range[1], total }),
          }}
        />
      </Card>

      {/* 创建/编辑权限模态框 */}
      <Modal
        title={editingPermission ? t('permissions.modal.editTitle') : t('permissions.modal.createTitle')}
        open={permissionModalVisible}
        onCancel={() => setPermissionModalVisible(false)}
        footer={null}
      >
        <Form
          form={permissionForm}
          layout="vertical"
          onFinish={handlePermissionSubmit}
        >
          <Form.Item
            name="name"
            label={t('permissions.permissionName')}
            rules={[{ required: true, message: t('permissions.enterPermissionName') }]}
          >
            <Input placeholder={t('permissions.enterPermissionName')} disabled={!!editingPermission} />
          </Form.Item>
          <Form.Item
            name="displayName"
            label={t('permissions.displayName')}
            rules={[{ required: true, message: t('permissions.enterDisplayName') }]}
          >
            <Input placeholder={t('permissions.enterDisplayName')} />
          </Form.Item>
          <Form.Item
            name="category"
            label={t('permissions.categoryTitle')}
            rules={[{ required: true, message: t('permissions.selectCategory') }]}
          >
            <Select placeholder={t('permissions.selectCategory')}>
              {categoryOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="description"
            label={t('permissions.description')}
          >
            <Input.TextArea placeholder={t('permissions.enterDescription')} rows={3} />
          </Form.Item>
          {editingPermission && (
            <Form.Item
              name="isActive"
              label={t('permissions.status')}
            >
              <Select placeholder={t('permissions.selectStatus')}>
                <Option value={true}>{t('common.active')}</Option>
                <Option value={false}>{t('common.inactive')}</Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingPermission ? t('common.update') : t('common.create')}
              </Button>
              <Button onClick={() => setPermissionModalVisible(false)}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PermissionsManager;
